import { useState, useEffect } from "react";
import { Upload, FileText, Trash2, User, Camera } from "lucide-react";
import { CameraModal } from "@/components/CameraModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  getFinancialTransactionAttachments,
  getFinancialTransactionAttachmentShow,
  storeFinancialTransactionAttachment,
  deleteFinancialTransactionAttachment,
} from "@/Api/financialTransactions";

interface AttachmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactionUuid: string;
}

interface AuthorProps {
  id: number;
  uuid: string;
  name: string;
  email: string;
}

interface TransactionTypeProps {
  id: number;
  name: string;
}

interface AttachmentProps {
  id: number;
  transaction_id: number;
  transaction_type_id: number;
  name: string;
  author_id: number;
  created_at: string;
  updated_at: string;
  file_path: string;
  author: AuthorProps;
  financial_transaction_type: TransactionTypeProps;
  file_type?: string;
  file_size?: string;
  url?: string;
}

export function AttachmentModal({
  isOpen,
  onClose,
  transactionUuid,
}: AttachmentModalProps) {
  const [attachments, setAttachments] = useState<AttachmentProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("view");
  const [fileName, setFileName] = useState<string>("");
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAttachments();
    }
  }, [isOpen, transactionUuid]);

  const fetchAttachments = async () => {
    if (transactionUuid && isOpen) {
      setLoading(true);
      try {
        const { data } = await getFinancialTransactionAttachments(
          transactionUuid
        );
        if (data.status) {
          setAttachments(data.response || []);
        }
      } catch (error) {
        toast.error("Erro ao carregar anexos");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileNameToUse = fileName.trim() || files[0].name;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", files[0]);
    formData.append("name", fileNameToUse);

    try {
      const { data } = await storeFinancialTransactionAttachment(
        transactionUuid,
        formData
      );
      if (data.status) {
        toast.success("Arquivo anexado com sucesso");
        fetchAttachments();
        setActiveTab("view");
        setFileName("");
      }
    } catch (error) {
      toast.error("Erro ao enviar o arquivo");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const handleCameraCapture = async (file: File) => {
    const fileNameToUse = fileName.trim() || file.name;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", fileNameToUse);

    try {
      const { data } = await storeFinancialTransactionAttachment(
        transactionUuid,
        formData
      );
      if (data.status) {
        toast.success("Foto anexada com sucesso");
        fetchAttachments();
        setActiveTab("view");
        setFileName("");
      }
    } catch (error) {
      toast.error("Erro ao enviar a foto");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    if (!confirm("Tem certeza que deseja excluir este anexo?")) return;

    try {
      const { data } = await deleteFinancialTransactionAttachment(attachmentId);
      if (data.status) {
        toast.success("Anexo removido com sucesso");
        setAttachments(
          attachments.filter((attachment) => attachment.id !== attachmentId)
        );
      } else {
        toast.error(data.message || "Erro ao remover anexo");
      }
    } catch (error) {
      toast.error("Erro ao remover o anexo");
    }
  };

  const viewAttachment = async (attachmentId: number) => {
    try {
      const { data } = await getFinancialTransactionAttachmentShow(
        attachmentId
      );
      if (data.status && data.response.file_path) {
        window.open(data.response.file_path, "_blank");
      } else {
        toast.error("Erro ao abrir o anexo");
      }
    } catch (error) {
      toast.error("Erro ao acessar o anexo");
    }
  };

  const formatFileSize = (size: string) => {
    const sizeNumber = parseInt(size);
    if (isNaN(sizeNumber)) return size;

    if (sizeNumber < 1024) return `${sizeNumber} B`;
    if (sizeNumber < 1024 * 1024) return `${(sizeNumber / 1024).toFixed(2)} KB`;
    return `${(sizeNumber / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Anexos da Transação</DialogTitle>
        </DialogHeader>

        <Tabs
          defaultValue="view"
          value={activeTab}
          onValueChange={setActiveTab}
        >
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="view" onClick={fetchAttachments}>
              Visualizar
            </TabsTrigger>
            <TabsTrigger value="upload">Anexar Novo</TabsTrigger>
          </TabsList>

          <TabsContent value="view" className="min-h-[200px]">
            {loading ? (
              <div className="flex justify-center items-center h-[200px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : attachments.length > 0 ? (
              <div 
                className="space-y-3 mt-2 overflow-y-auto pr-1" 
                style={{ maxHeight: attachments.length > 3 ? '350px' : 'auto' }}
              >
                {attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex flex-col p-3 border rounded-md"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-blue-500" />
                        <div>
                          <p className="text-sm font-medium truncate max-w-[180px]">
                            {attachment.name}
                          </p>
                          {attachment.file_size && (
                            <p className="text-xs text-muted-foreground">
                              {formatFileSize(attachment.file_size)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-primary hover:text-primary/80 hover:bg-primary/10 rounded-md"
                          onClick={() => viewAttachment(attachment.id)}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteAttachment(attachment.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-col text-xs text-muted-foreground border-t pt-2">
                      <div className="flex items-center gap-1 mt-1">
                        <User className="h-3 w-3" />
                        <span>Autor: {attachment.author?.name || "Não informado"}</span>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span>Tipo: {attachment.financial_transaction_type?.name || "Não informado"}</span>
                        <span>Data: {formatDate(attachment.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px] text-center">
                <FileText className="h-10 w-10 text-muted-foreground mb-2" />
                <p className="text-muted-foreground">Nenhum anexo encontrado</p>
                <Button
                  variant="link"
                  className="mt-2"
                  onClick={() => setActiveTab("upload")}
                >
                  Adicionar agora
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="upload" className="min-h-[200px]">
            <div className="space-y-4">
              <div className="mb-4">
                <label
                  htmlFor="file-name"
                  className="block text-sm font-medium mb-1"
                >
                  Nome do Anexo
                </label>
                <input
                  type="text"
                  id="file-name"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Digite um nome para o arquivo"
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  disabled={uploading}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Opcional. Se não informado, será usado o nome original do
                  arquivo.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-md">
                  <Upload className="h-10 w-10 text-muted-foreground mb-2" />
                  <p className="text-sm text-center mb-2">
                    Arraste e solte arquivos aqui ou clique para selecionar
                  </p>
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                  <label
                    htmlFor="file-upload"
                    className={`
                      inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium
                      transition-colors focus-visible:outline-none focus-visible:ring-1
                      focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50
                      bg-primary text-primary-foreground shadow hover:bg-primary/90
                      h-9 px-4 py-2 cursor-pointer
                    `}
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Enviando...
                      </>
                    ) : (
                      "Selecionar Arquivo"
                    )}
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => setIsCameraOpen(true)}
                  disabled={uploading}
                  className="flex flex-col items-center justify-center h-[140px] w-24 border-2 border-dashed rounded-md cursor-pointer hover:border-primary/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Tirar foto"
                >
                  <Camera className="w-8 h-8 mb-1 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground font-semibold">
                    Tirar foto
                  </p>
                </button>
              </div>

              <div className="text-xs text-muted-foreground">
                <p>Formatos suportados: PDF, JPEG, PNG, DOC, DOCX, XLS, XLSX</p>
                <p>Tamanho máximo: 10MB</p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <CameraModal
          isOpen={isCameraOpen}
          onClose={() => setIsCameraOpen(false)}
          onCapture={handleCameraCapture}
        />
      </DialogContent>
    </Dialog>
  );
}
